import React from 'react';
import { ShieldCheck, Compass, Database, Workflow, Share2, Lock, UserCheck, Building2, Archive, HelpCircle, RefreshCw } from 'lucide-react';
import './HipaaFaq.css';

const policyIconProps = { size: 20, color: 'var(--primary-color)' };

const privacyPolicySections = [
    {
        question: "1. Scope and Purpose",
        icon: <Compass {...policyIconProps} />,
        answer: (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <p>Physicians Health Portal, including its PHP/eMAR functions, is a facility-scoped clinical workspace operated by Bridge Technologies LLC. The system may be used by authorized clinics and facilities to support patient registry management, appointments, patient visits, evaluation sessions, medication-related workflows, therapy documentation, notes, secure messages, and practice operations.</p>
                <p>This policy explains how PHP/eMAR is intended to handle information submitted to or generated within the system. It applies to authorized users, administrators, and participating organizations using the platform.</p>
            </div>
        )
    },
    {
        question: "2. Information the System Handles",
        icon: <Database {...policyIconProps} />,
        answer: (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <p>Depending on a clinic or facility's configuration and use of the system, PHP/eMAR may process information such as:</p>
                <ul style={{ margin: 0, paddingLeft: 18, display: 'grid', gap: 8 }}>
                    <li>Patient identifiers, contact details, and demographic information.</li>
                    <li>Appointments, visits, evaluation, therapy, care, and medication-related information.</li>
                    <li>Clinical notes, diagnoses, treatment documentation, attachments, and care coordination information.</li>
                    <li>User account details, role assignments, secure-message content, and administrative activity records.</li>
                    <li>Technical information generated during use of the system, such as access, session, device, browser, and audit-oriented activity information.</li>
                </ul>
                <p>When information identifies a patient and relates to health, healthcare, payment, or healthcare operations, it may be protected health information (PHI). Electronic PHI is referred to as ePHI.</p>
            </div>
        )
    },
    {
        question: "3. How Information Is Used",
        icon: <Workflow {...policyIconProps} />,
        answer: (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <p>PHP/eMAR is designed to use information only as needed to provide, maintain, secure, support, and improve the system and the services requested by the participating organization. Permitted system-level uses may include:</p>
                <ul style={{ margin: 0, paddingLeft: 18, display: 'grid', gap: 8 }}>
                    <li>Providing requested clinical-workspace features and authorized user access.</li>
                    <li>Supporting configuration, troubleshooting, user assistance, backup, maintenance, and service reliability.</li>
                    <li>Protecting the system, investigating suspected misuse or security incidents, and maintaining audit records.</li>
                    <li>Meeting applicable legal obligations and honoring the applicable service agreement or Business Associate Agreement.</li>
                </ul>
                <p><strong>Bridge Technologies LLC does not sell PHI.</strong> PHP/eMAR does not use PHI for unrelated advertising or marketing.</p>
            </div>
        )
    },
    {
        question: "4. Access and Information Sharing",
        icon: <Share2 {...policyIconProps} />,
        answer: (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <p>PHP/eMAR is designed for access by users authorized by the participating clinic or facility. Organizations are responsible for deciding who receives access, assigning appropriate roles, and promptly updating or removing access when job duties or authorization change. Users must access only the information needed for legitimate, authorized duties.</p>
                <p>Bridge Technologies LLC may access information only when reasonably necessary to operate, secure, support, or maintain PHP/eMAR; to respond to an authorized customer request; or as otherwise permitted or required by law and the applicable agreement.</p>
                <p>Information may be shared with service providers that help operate or secure the system when necessary and subject to appropriate contractual and legal safeguards. Where required, Bridge Technologies LLC seeks Business Associate Agreements or equivalent contractual protections.</p>
            </div>
        )
    },
    {
        question: "5. Security Practices",
        icon: <Lock {...policyIconProps} />,
        answer: (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <p>PHP/eMAR is designed to use administrative, technical, and operational safeguards intended to help protect the confidentiality, integrity, and availability of information. These measures may include:</p>
                <ul style={{ margin: 0, paddingLeft: 18, display: 'grid', gap: 8 }}>
                    <li>Unique user authentication, role-based access, and account-security controls.</li>
                    <li>Facility- and practice-scoped workspace controls intended to limit cross-organization access.</li>
                    <li>Encryption protections for data in transit and, where supported by the underlying architecture, at rest.</li>
                    <li>Audit-oriented activity records and security monitoring practices.</li>
                    <li>System maintenance, backups, recovery practices, and incident-response procedures appropriate to the service architecture.</li>
                </ul>
                <p>No system can guarantee absolute security. Users and participating organizations must protect credentials, secure their devices, use trusted networks, keep systems current, and report suspected unauthorized access promptly.</p>
            </div>
        )
    },
    {
        question: "6. Your Responsibilities as a User",
        icon: <UserCheck {...policyIconProps} />,
        answer: (
            <ul style={{ margin: 0, paddingLeft: 18, display: 'grid', gap: 8 }}>
                <li>Do not share accounts, passwords, verification methods, or active sessions.</li>
                <li>Use PHP/eMAR only for authorized work and only access the minimum information needed.</li>
                <li>Confirm recipients before sending messages, records, or other patient information.</li>
                <li>Do not place PHI into personal email, consumer messaging applications, unapproved storage, or unapproved third-party tools.</li>
                <li>Promptly report suspected account compromise, misdirected information, inappropriate access, or another potential incident to your organization and php@bridgetechnologies.org.</li>
            </ul>
        )
    },
    {
        question: "7. Clinic and Facility Responsibilities",
        icon: <Building2 {...policyIconProps} />,
        answer: (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <p>Each participating clinic or facility is independently responsible for its own privacy and compliance program. This includes maintaining a Notice of Privacy Practices where required, obtaining required consents and authorizations, responding to patient-rights requests, training its workforce, managing its internal policies, and complying with HIPAA and other applicable laws.</p>
                <p>The clinic or facility controls its own patient records and determines the permitted uses and disclosures of PHI. It is also responsible for confirming that its own vendors, workforce members, and workflows meet applicable legal and contractual requirements.</p>
            </div>
        )
    },
    {
        question: "8. Data Retention and System Termination",
        icon: <Archive {...policyIconProps} />,
        answer: (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <p>PHP/eMAR retains information in accordance with applicable service terms, legal requirements, and the participating organization's instructions, subject to technical and legal limitations. Clinics and facilities are responsible for determining their own record-retention requirements and for requesting needed records or exports before termination when appropriate.</p>
                <p>Upon termination, information will be handled in accordance with the applicable agreement and legal requirements, including return or destruction when required and feasible.</p>
            </div>
        )
    },
    {
        question: "9. Privacy Requests and Questions",
        icon: <HelpCircle {...policyIconProps} />,
        answer: (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <p>Patients should direct requests about their records, consent, corrections, restrictions, or a clinic's privacy practices to the clinic or facility that provides their care. PHP/eMAR supports participating organizations in responding to appropriate requests when required by an applicable agreement.</p>
                <p>For system privacy or security questions, or to report a potential issue involving PHP/eMAR, contact:</p>
                <div style={{ padding: 12, backgroundColor: 'var(--bg-secondary)', borderRadius: 8, borderLeft: '4px solid var(--primary-color)' }}>
                    <strong style={{ display: 'block' }}>Physicians Health Portal</strong>
                    <span style={{ fontSize: 13 }}>Operated by Bridge Technologies LLC</span><br />
                    <a href="mailto:php@bridgetechnologies.org" style={{ color: 'var(--primary-color)', fontWeight: 600 }}>php@bridgetechnologies.org</a>
                </div>
            </div>
        )
    },
    {
        question: "10. Changes to This Policy",
        icon: <RefreshCw {...policyIconProps} />,
        answer: (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <p>Bridge Technologies LLC may update this System Privacy Policy as PHP/eMAR evolves, laws change, or system practices improve. The current version will be made available through the PHP/eMAR workspace or another reasonable customer communication channel.</p>
                <p style={{ fontSize: 12, color: 'var(--text-secondary)' }}>© 2026 Bridge Technologies LLC. All rights reserved.</p>
            </div>
        )
    }
];

export default function PrivacyPolicy() {
    return (
        <section className="hipaa-faq">
            <div className="page-heading">
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <div style={{ padding: 12, backgroundColor: 'var(--primary-color)', borderRadius: 12, color: 'white' }}>
                        <ShieldCheck size={32} />
                    </div>
                    <div>
                        <div className="eyebrow">PRIVACY & TRANSPARENCY</div>
                        <h1 style={{ margin: 0 }}>System Privacy Policy</h1>
                        <p style={{ margin: 0 }}>How information is handled within the PHP/eMAR system.</p>
                    </div>
                </div>
            </div>

            <div className="content-card" style={{ padding: '20px', marginBottom: 20, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16 }}>
                <div>
                    <div className="eyebrow" style={{ marginBottom: 4 }}>Effective date</div>
                    <strong>September 26, 2026</strong>
                </div>
                <div>
                    <div className="eyebrow" style={{ marginBottom: 4 }}>System operator</div>
                    <strong>Bridge Technologies LLC</strong>
                </div>
                <div>
                    <div className="eyebrow" style={{ marginBottom: 4 }}>System</div>
                    <strong>Physicians Health Portal / PHP eMAR</strong>
                </div>
                <div>
                    <div className="eyebrow" style={{ marginBottom: 4 }}>Privacy contact</div>
                    <a href="mailto:php@bridgetechnologies.org" style={{ color: 'var(--primary-color)', fontWeight: 600 }}>php@bridgetechnologies.org</a>
                </div>
            </div>

            <p style={{ margin: '0 0 20px 0', color: 'var(--text-secondary)', lineHeight: 1.7, fontSize: 14 }}>
                This policy applies to authorized users of the PHP/eMAR system and explains the system-level handling of information. It is not a substitute for a clinic or facility's patient-facing privacy notice, Business Associate Agreement, service agreement, or legal advice.
            </p>

            <div className="hipaa-questions">
                {privacyPolicySections.map((s, i) => (
                    <details className="content-card" key={i} open={i === 0}>
                        <summary style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}>
                            {s.icon}
                            <span>{s.question}</span>
                        </summary>
                        <div className="hipaa-answer" style={{ marginTop: 16, paddingBottom: 8 }}>
                            {s.answer}
                        </div>
                    </details>
                ))}
            </div>

            <div className="compliance-footer" style={{ marginTop: 20 }}>
                <div className="compliance-disclaimer">
                    <h3>Responsibility Notice</h3>
                    <p>This policy describes the Physicians Health Portal / PHP eMAR system. Each clinic or facility remains responsible for its own Notice of Privacy Practices, consent processes, policies, workforce training, and compliance obligations.</p>
                </div>
            </div>
        </section>
    );
}
